import { SchemaTransformerArgs } from '@web-config-app/core';
import type { RulesLogic } from 'json-logic-js';
import jsonLogic from 'json-logic-js';
import { getConditionOfType } from '../get-condition-of-type/get-condition-of-type';

const evaluateRules = (
  rules: RulesLogic,
  data: any,
  {
    arrayItem,
    onSchemaPropertyRemoved,
    path,
    schemaPropertyName,
    rootData,
    featureFlags,
  }: SchemaTransformerArgs['options'] = {},
) => {
  const baseData =
    arrayItem?.isArrayItemContext !== true ? data : arrayItem.arrayItemData;

  // Merge rootData under `$` key and featureFlags under `_featureFlags` key for JsonLogic var operator
  const dataToEvaluate = {
    ...baseData,
    ...(rootData !== undefined && { $: rootData }),
    ...(featureFlags !== undefined && { _featureFlags: featureFlags }),
  };

  const result = jsonLogic.apply(rules, dataToEvaluate);

  // When omitIf evaluates to true (field should be omitted), notify the consumer so it can
  // unset/clean up any corresponding form state.
  if (
    result === true &&
    onSchemaPropertyRemoved &&
    typeof path === 'string' &&
    schemaPropertyName
  ) {
    onSchemaPropertyRemoved(path, schemaPropertyName);
  }

  return result;
};

/**
 * The evaluateOmitIfCondition function is a part of the x-entity-conditional annotation used to evaluate the omitIf condition.
 *
 * This is used to determine if a field should be omitted based on the current data state.
 * When the condition evaluates to `true`, consumers typically:
 * - Hide the field from the UI (via schema transformation)
 * - Remove it from API payloads before sending to the backend (via request sanitization)
 * - Optionally unset any corresponding form state via `options.onSchemaPropertyRemoved`
 *
 * @param schema - the schema that implements or extends the AnnotatedJsonSchema interface
 * @param data - data that corresponds to the schema
 * @param options - schema transformer options
 * @returns true if the field should be omitted, false otherwise
 */
export const evaluateOmitIfCondition = ({
  schema,
  data,
  options,
}: SchemaTransformerArgs): boolean => {
  if (!schema['x-entity-conditional']) return false;

  const omitIfCondition = getConditionOfType(schema, 'omitIf');

  if (!omitIfCondition) return false;

  return evaluateRules(omitIfCondition.rules, data, options) === true;
};
